import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ServiciosSapFacade } from '../services/servicios-sap.facade';
import { TablaServiciosSapComponent } from '../components/tabla-servicios-sap/tabla-servicios-sap.component';
import { ServicioSapDialogComponent } from '../components/servicio-sap-dialog/servicio-sap-dialog.component';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import {
  SERVICIO_SAP_CODIGO_MAX_LENGTH,
  SERVICIO_SAP_DESCRIPCION_MAX_LENGTH
} from 'src/app/common/constants-ejb';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';

/**
 * Container (smart component) de "Administración › Maestro › Servicios SAP" (HU1287272).
 *
 * <p>Solo orquesta la UI y delega en {@link ServiciosSapFacade}. Filtro plegable, tabla en `p-card`
 * oculta hasta buscar y paginación manual por cursor (nunca `[paginator]="true"`).</p>
 *
 * <p>Legacy: AdminServiciosSapViewImpl / AdminServiciosSapActivity.</p>
 */
@Component({
  selector: 'app-servicios-sap',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    TablaServiciosSapComponent,
    ServicioSapDialogComponent,
    BusquedaPaginadorComponent
  ],
  providers: [ServiciosSapFacade, ConfirmationService],
  template: `
    <p-confirmDialog></p-confirmDialog>
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1
          class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100"
        >
          Servicios SAP
        </h1>
        <p-button
          label="Nuevo"
          icon="pi pi-plus"
          styleClass="min-h-[44px]"
          (onClick)="facade.abrirNuevo()"
        ></p-button>
      </div>

      <p-fieldset
        legend="Filtro Consulta de Servicios SAP"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-3 mb-3"
        >
          <div>
            <label
              for="filtro-codigo"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Código servicio</label
            >
            <input
              id="filtro-codigo"
              pInputText
              class="w-full"
              inputmode="numeric"
              autocomplete="off"
              [maxlength]="codigoMaxLength"
              [(ngModel)]="facade.filtro.codigo"
              (keypress)="soloNumeros($event)"
              (input)="
                facade.filtro.codigo = sanitizeNumbersOnly(facade.filtro.codigo)
              "
              (keyup.enter)="facade.buscar()"
            />
          </div>
          <div>
            <label
              for="filtro-descripcion"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Descripción del servicio</label
            >
            <input
              id="filtro-descripcion"
              pInputText
              class="w-full"
              autocomplete="off"
              [maxlength]="descripcionMaxLength"
              [(ngModel)]="facade.filtro.descripcion"
              (input)="
                facade.filtro.descripcion = sanitizeTextPreserveSpaces(
                  facade.filtro.descripcion
                )
              "
              (keyup.enter)="facade.buscar()"
            />
          </div>
        </div>

        <div
          class="flex flex-wrap gap-2 sm:gap-3 justify-end sm:justify-start w-full"
        >
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="facade.limpiarFiltros()"
          ></p-button>
          <p-button
            label="Buscar"
            icon="pi pi-search"
            styleClass="min-h-[44px]"
            [loading]="facade.loading()"
            (onClick)="facade.buscar()"
          ></p-button>
        </div>
      </p-fieldset>

      @if (facade.mensaje()) {
        <div
          class="p-3 mb-4 rounded border"
          role="alert"
          [ngClass]="{
            'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700':
              facade.mensajeTipo() === 'success',
            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700':
              facade.mensajeTipo() === 'warning',
            'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700':
              facade.mensajeTipo() === 'error',
            'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700':
              facade.mensajeTipo() === 'info'
          }"
        >
          {{ facade.mensaje() }}
        </div>
      }

      @if (facade.loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      @if (facade.busquedaRealizada() && !facade.loading()) {
        <p-card styleClass="mb-4">
          <ng-template pTemplate="header">
            <div
              class="p-3 pb-0 flex justify-between items-center flex-wrap gap-2"
            >
              <h3
                class="text-lg font-semibold text-gray-700 dark:text-gray-200 m-0"
              >
                Listado de Servicios SAP
              </h3>
              @if (facade.totalElements() > 0) {
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  Registros del {{ facade.registroInicio() }} hasta el
                  {{ facade.registroFin() }} de {{ facade.totalElements() }}
                </span>
              }
            </div>
          </ng-template>

          <app-tabla-servicios-sap
            [items]="facade.items()"
            [loading]="facade.loading()"
            (editar)="facade.abrirEditar($event)"
            (eliminar)="facade.confirmarEliminar($event)"
          >
          </app-tabla-servicios-sap>

          @if (facade.totalElements() > 0) {
            <app-busqueda-paginador
              [currentPage]="facade.currentPage()"
              [totalPages]="facade.totalPages()"
              [hasPrevious]="facade.hasPrevious()"
              [hasNext]="facade.hasNext()"
              [pageSizeOptions]="facade.pageSizeOptions"
              [selectedPageSize]="facade.pageSize()"
              (first)="facade.irPrimeraPagina()"
              (previous)="facade.irPaginaAnterior()"
              (next)="facade.irPaginaSiguiente()"
              (last)="facade.irUltimaPagina()"
            >
            </app-busqueda-paginador>
          }
        </p-card>
      }
    </div>

    @if (facade.dialogoVisible()) {
      <app-servicio-sap-dialog
        [modo]="facade.modoDialogo()"
        [servicio]="facade.servicioEditando()"
        [mensaje]="facade.mensajeDialogo()"
        [mensajeTipo]="
          facade.mensajeDialogoTipo() === 'error' ? 'error' : 'warning'
        "
        [guardando]="facade.guardando()"
        (aceptar)="facade.guardar($event)"
        (cancelar)="facade.cerrarDialogo()"
      >
      </app-servicio-sap-dialog>
    }
  `
})
export class ServiciosSapComponent {
  protected readonly facade = inject(ServiciosSapFacade);

  protected readonly codigoMaxLength = SERVICIO_SAP_CODIGO_MAX_LENGTH;
  protected readonly descripcionMaxLength = SERVICIO_SAP_DESCRIPCION_MAX_LENGTH;
  protected readonly sanitizeNumbersOnly = sanitizeNumbersOnly;
  protected readonly sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;
  protected readonly soloNumeros = onlyNumbersKeypress;
}
